/**
 * Financial ratios from statement figures, grouped as in an introductory accounting text
 * (OpenStax Principles of Finance, ch. 6, and Principles of Accounting): liquidity, solvency, efficiency,
 * profitability and market.
 *
 * Every input is optional; a ratio is `null` when a figure it needs is missing or its
 * denominator is zero. Turnover and return ratios use average balances ((opening +
 * closing) ÷ 2) when `useAverage` is set and the opening figure was given, otherwise
 * the closing balance — the two conventions differ between textbooks. All sales are
 * treated as credit sales for the receivables ratios.
 */

export interface Statements {
  cash: number | null;
  securities: number | null;
  receivables: number | null;
  inventory: number | null;
  payables: number | null;
  currentAssets: number | null;
  totalAssets: number | null;
  currentLiabilities: number | null;
  totalLiabilities: number | null;
  /** Interest-bearing debt. */
  debt: number | null;
  equity: number | null;
  revenue: number | null;
  cogs: number | null;
  /** Operating income (EBIT). */
  ebit: number | null;
  interest: number | null;
  netIncome: number | null;
  price: number | null;
  shares: number | null;
  /** Total common dividends paid. */
  dividends: number | null;
  /** Opening balances, for averages. */
  priorReceivables: number | null;
  priorInventory: number | null;
  priorPayables: number | null;
  priorAssets: number | null;
  priorEquity: number | null;
}

export type Unit = "ratio" | "percent" | "days" | "money";

export type RatioGroup =
  | "liquidity"
  | "solvency"
  | "efficiency"
  | "profitability"
  | "market";

export const RATIO_GROUPS: readonly RatioGroup[] = [
  "liquidity",
  "solvency",
  "efficiency",
  "profitability",
  "market",
];

export type RatioKey =
  | "currentRatio"
  | "quickRatio"
  | "quickRatioAlt"
  | "cashRatio"
  | "workingCapital"
  | "debtToEquity"
  | "debtRatio"
  | "equityMultiplier"
  | "interestBearingDebtToEquity"
  | "interestCoverage"
  | "assetTurnover"
  | "receivablesTurnover"
  | "daysSales"
  | "inventoryTurnover"
  | "daysInventory"
  | "payablesTurnover"
  | "daysPayable"
  | "cashConversionCycle"
  | "grossMargin"
  | "operatingMargin"
  | "netMargin"
  | "returnOnAssets"
  | "returnOnEquity"
  | "earningsPerShare"
  | "priceEarnings"
  | "dividendPerShare"
  | "dividendYield"
  | "payoutRatio"
  | "bookValuePerShare"
  | "priceToBook"
  | "marketCap";

export interface RatioDef {
  key: RatioKey;
  group: RatioGroup;
  unit: Unit;
}

export const RATIO_DEFS: readonly RatioDef[] = [
  { key: "currentRatio", group: "liquidity", unit: "ratio" },
  { key: "quickRatio", group: "liquidity", unit: "ratio" },
  { key: "quickRatioAlt", group: "liquidity", unit: "ratio" },
  { key: "cashRatio", group: "liquidity", unit: "ratio" },
  { key: "workingCapital", group: "liquidity", unit: "money" },
  { key: "debtToEquity", group: "solvency", unit: "ratio" },
  { key: "debtRatio", group: "solvency", unit: "ratio" },
  { key: "equityMultiplier", group: "solvency", unit: "ratio" },
  { key: "interestBearingDebtToEquity", group: "solvency", unit: "ratio" },
  { key: "interestCoverage", group: "solvency", unit: "ratio" },
  { key: "assetTurnover", group: "efficiency", unit: "ratio" },
  { key: "receivablesTurnover", group: "efficiency", unit: "ratio" },
  { key: "daysSales", group: "efficiency", unit: "days" },
  { key: "inventoryTurnover", group: "efficiency", unit: "ratio" },
  { key: "daysInventory", group: "efficiency", unit: "days" },
  { key: "payablesTurnover", group: "efficiency", unit: "ratio" },
  { key: "daysPayable", group: "efficiency", unit: "days" },
  { key: "cashConversionCycle", group: "efficiency", unit: "days" },
  { key: "grossMargin", group: "profitability", unit: "percent" },
  { key: "operatingMargin", group: "profitability", unit: "percent" },
  { key: "netMargin", group: "profitability", unit: "percent" },
  { key: "returnOnAssets", group: "profitability", unit: "percent" },
  { key: "returnOnEquity", group: "profitability", unit: "percent" },
  { key: "earningsPerShare", group: "market", unit: "money" },
  { key: "priceEarnings", group: "market", unit: "ratio" },
  { key: "dividendPerShare", group: "market", unit: "money" },
  { key: "dividendYield", group: "market", unit: "percent" },
  { key: "payoutRatio", group: "market", unit: "percent" },
  { key: "bookValuePerShare", group: "market", unit: "money" },
  { key: "priceToBook", group: "market", unit: "ratio" },
  { key: "marketCap", group: "market", unit: "money" },
];

export interface RatioOptions {
  useAverage: boolean;
  /** 365 or 360. */
  daysInYear: number;
}

export interface RatioOutput {
  /** Percentages are returned as decimals (0.25 = 25%). */
  values: Record<RatioKey, number | null>;
  /** True when averages were requested but an opening balance was missing. */
  usedEndingFallback: boolean;
  dupont: {
    margin: number;
    turnover: number;
    multiplier: number;
    roe: number;
  } | null;
}

type Num = number | null;

function div(a: Num, b: Num): Num {
  if (a === null || b === null || b === 0) {
    return null;
  }
  return a / b;
}

function sum(...parts: Num[]): Num {
  let total = 0;
  for (const part of parts) {
    if (part === null) {
      return null;
    }
    total += part;
  }
  return total;
}

function diff(a: Num, b: Num): Num {
  return a === null || b === null ? null : a - b;
}

function product(a: Num, b: Num): Num {
  return a === null || b === null ? null : a * b;
}

export function computeRatios(
  s: Statements,
  options: RatioOptions
): RatioOutput {
  let fallback = false;
  const balance = (end: Num, prior: Num): Num => {
    if (end === null || !options.useAverage) {
      return end;
    }
    if (prior === null) {
      fallback = true;
      return end;
    }
    return (end + prior) / 2;
  };
  const days = options.daysInYear;
  const avgAssets = () => balance(s.totalAssets, s.priorAssets);
  const avgEquity = () => balance(s.equity, s.priorEquity);

  const quickAssets = sum(s.cash, s.receivables, s.securities ?? 0);
  const cashAssets = s.cash === null ? null : s.cash + (s.securities ?? 0);
  const grossProfit = diff(s.revenue, s.cogs);

  const assetTurnover = div(s.revenue, avgAssets());
  const receivablesTurnover = div(
    s.revenue,
    balance(s.receivables, s.priorReceivables)
  );
  const inventoryTurnover = div(s.cogs, balance(s.inventory, s.priorInventory));
  const payablesTurnover = div(s.cogs, balance(s.payables, s.priorPayables));
  const daysSales = div(days, receivablesTurnover);
  const daysInventory = div(days, inventoryTurnover);
  const daysPayable = div(days, payablesTurnover);

  const eps = div(s.netIncome, s.shares);
  const dps = div(s.dividends, s.shares);
  const bookPerShare = div(s.equity, s.shares);

  const values: Record<RatioKey, Num> = {
    currentRatio: div(s.currentAssets, s.currentLiabilities),
    quickRatio: div(quickAssets, s.currentLiabilities),
    quickRatioAlt: div(
      diff(s.currentAssets, s.inventory),
      s.currentLiabilities
    ),
    cashRatio: div(cashAssets, s.currentLiabilities),
    workingCapital: diff(s.currentAssets, s.currentLiabilities),
    debtToEquity: div(s.totalLiabilities, s.equity),
    debtRatio: div(s.totalLiabilities, s.totalAssets),
    equityMultiplier: div(s.totalAssets, s.equity),
    interestBearingDebtToEquity: div(s.debt, s.equity),
    interestCoverage: div(s.ebit, s.interest),
    assetTurnover,
    receivablesTurnover,
    daysSales,
    inventoryTurnover,
    daysInventory,
    payablesTurnover,
    daysPayable,
    cashConversionCycle: diff(sum(daysInventory, daysSales), daysPayable),
    grossMargin: div(grossProfit, s.revenue),
    operatingMargin: div(s.ebit, s.revenue),
    netMargin: div(s.netIncome, s.revenue),
    returnOnAssets: div(s.netIncome, avgAssets()),
    returnOnEquity: div(s.netIncome, avgEquity()),
    earningsPerShare: eps,
    priceEarnings: div(s.price, eps),
    dividendPerShare: dps,
    dividendYield: div(dps, s.price),
    payoutRatio: div(s.dividends, s.netIncome),
    bookValuePerShare: bookPerShare,
    priceToBook: div(s.price, bookPerShare),
    marketCap: product(s.price, s.shares),
  };

  const margin = values.netMargin;
  const multiplier = div(avgAssets(), avgEquity());
  const roe = values.returnOnEquity;
  const dupont =
    margin !== null &&
    assetTurnover !== null &&
    multiplier !== null &&
    roe !== null
      ? { margin, turnover: assetTurnover, multiplier, roe }
      : null;

  return { values, usedEndingFallback: fallback, dupont };
}
